/**
 * Opt-in probe for intermittent telemetry field wipes on the robot page.
 *
 * Enable:
 *   localStorage.setItem('fmsTelemWipeDebug', '1')
 *   // or ?telemwipedebug=1 then reload
 *
 * Then open an Autonomics robot page and wait for flicker.
 * Dump evidence:
 *   copy(window.__FMS_TELEM_WIPE__.events)
 *   window.__FMS_TELEM_WIPE__.snapshot()
 */

const DEBUG_STORAGE_KEY = 'fmsTelemWipeDebug'
const MAX_EVENTS = 200

/** UI fields that blink when cleared between WS frames. */
export const TELEM_WIPE_WATCH_FIELDS = [
  'waterTankFilling',
  'dirtyWaterTankFilling',
  'temperatureControlUnit',
  'temperatureChassis',
  'temperatureLidar',
  'temperatureExternal',
  'trashFullness',
  'batteryPower',
] as const

export type TelemWipeWatchField = (typeof TELEM_WIPE_WATCH_FIELDS)[number]

export type TelemWipeEvent = {
  at: string
  atMs: number
  serial: string | null
  vendor: string | null
  timestampBefore: number | null | undefined
  timestampAfter: number | null | undefined
  payloadKeys: string[]
  /** Keys present on the setTelemetry payload (incl. undefined). */
  watchedInPayload: Partial<Record<TelemWipeWatchField, { inPayload: boolean; value: unknown }>>
  wipes: Array<{
    field: TelemWipeWatchField
    from: unknown
    to: unknown
    reason: 'payload_undefined' | 'payload_null' | 'object_assign' | 'unknown'
  }>
}

type TelemWipeApi = {
  enabled: () => boolean
  enable: () => void
  disable: () => void
  clear: () => void
  events: TelemWipeEvent[]
  snapshot: () => {
    enabled: boolean
    eventCount: number
    lastWipeAt: string | null
    last: TelemWipeEvent | null
  }
}

declare global {
  interface Window {
    __FMS_TELEM_WIPE__?: TelemWipeApi
  }
}

const events: TelemWipeEvent[] = []

export function telemWipeDebugEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false
    if (window.localStorage?.getItem(DEBUG_STORAGE_KEY) === '1') return true
    return new URLSearchParams(window.location.search).get('telemwipedebug') === '1'
  } catch {
    return false
  }
}

function isPresentSensorValue(value: unknown): boolean {
  return value !== undefined && value !== null
}

function classifyWipeReason(
  field: TelemWipeWatchField,
  telemetryFields: Record<string, unknown>
): TelemWipeEvent['wipes'][number]['reason'] {
  if (!Object.prototype.hasOwnProperty.call(telemetryFields, field)) {
    return 'unknown'
  }
  const value = telemetryFields[field]
  if (value === undefined) return 'payload_undefined'
  if (value === null) return 'payload_null'
  return 'object_assign'
}

export function noteTelemetryWipesFromSetTelemetry(args: {
  prevTelemetry: Record<string, unknown> | null | undefined
  nextTelemetry: Record<string, unknown> | null | undefined
  telemetryFields: Record<string, unknown>
  serial?: string | null
  vendor?: string | null
}): TelemWipeEvent | null {
  if (!telemWipeDebugEnabled()) return null

  const prev = args.prevTelemetry || {}
  const next = args.nextTelemetry || {}
  const watchedInPayload: TelemWipeEvent['watchedInPayload'] = {}
  const wipes: TelemWipeEvent['wipes'] = []

  for (const field of TELEM_WIPE_WATCH_FIELDS) {
    const inPayload = Object.prototype.hasOwnProperty.call(args.telemetryFields, field)
    watchedInPayload[field] = {
      inPayload,
      value: inPayload ? args.telemetryFields[field] : undefined,
    }

    const from = prev[field]
    const to = next[field]
    if (isPresentSensorValue(from) && !isPresentSensorValue(to)) {
      wipes.push({
        field,
        from,
        to,
        reason: classifyWipeReason(field, args.telemetryFields),
      })
    }
  }

  if (!wipes.length) return null

  const event: TelemWipeEvent = {
    at: new Date().toISOString(),
    atMs: Date.now(),
    serial: args.serial ?? null,
    vendor: args.vendor ?? null,
    timestampBefore: prev.timestamp as number | null | undefined,
    timestampAfter: next.timestamp as number | null | undefined,
    payloadKeys: Object.keys(args.telemetryFields),
    watchedInPayload,
    wipes,
  }

  events.push(event)
  if (events.length > MAX_EVENTS) {
    events.splice(0, events.length - MAX_EVENTS)
  }

  // eslint-disable-next-line no-console
  console.warn(
    '[TELEM WIPE]',
    wipes.map(w => `${w.field}: ${String(w.from)} → ${String(w.to)} (${w.reason})`).join('; '),
    event
  )

  return event
}

export function installTelemWipeDebugApi(): void {
  if (typeof window === 'undefined') return
  window.__FMS_TELEM_WIPE__ = {
    enabled: telemWipeDebugEnabled,
    enable: () => {
      window.localStorage.setItem(DEBUG_STORAGE_KEY, '1')
      // eslint-disable-next-line no-console
      console.info('[TELEM WIPE] debug on; reload robot page, then copy(__FMS_TELEM_WIPE__.events)')
    },
    disable: () => {
      window.localStorage.removeItem(DEBUG_STORAGE_KEY)
    },
    clear: () => {
      events.length = 0
    },
    get events() {
      return events
    },
    snapshot: () => ({
      enabled: telemWipeDebugEnabled(),
      eventCount: events.length,
      lastWipeAt: events.length ? events[events.length - 1].at : null,
      last: events.length ? events[events.length - 1] : null,
    }),
  }
}
